import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InfoTip } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import {
  initialQcRecordForm, useQcRecordOptions, qcRecordFormGaps, saveQcRecord,
  QcTypeSection, QcCoverageSection, QcAnswersSection, QcAttachmentsSection, QcApprovalDateSection,
} from '../components/records/qcRecordKind';
import { useLegacyImportSetting } from '../components/records/legacyImport';
import { useQcPrefill } from '../components/workflow/QcPrefill';

// Management — 建一筆 QC6/QC7 正式記錄。
//
// 版面與步驟刻意與 `New QC6 / QC7 Acceptance request`（WorkflowQcNew）一致:
// ① 挑 engagement → ② 型別 → ③ 問卷 → ④ 附件與備註。差別只有兩點,而且都是
// 「管理面沒有審核」的必然結果:
//
//   · 沒有法定人數提示、沒有 QC7 的第二位核准人——這裡不會有人審,建立當下
//     就是定案。
//   · 送出鈕寫 Create record 而不是 Submit request。
//
// 建立會**回寫涵蓋案的 qc_type / qc_record_id**（與 QCR 核准同一段效果),所以
// 這頁做完,那幾案的 Current stage 就過了 QC 那一關。
const QMQcRecordNew = () => {
  const navigate = useNavigate();
  const { engagements, optionsError } = useQcRecordOptions();
  const { enabled: legacyEnabled, missingSignedFormAllowed } = useLegacyImportSetting();

  const [form, setForm] = useState(initialQcRecordForm(null));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const selected = useMemo(
    () => form.engagementIds.map((id) => engagements.find((e) => e.id === id)).filter(Boolean),
    [form.engagementIds, engagements]
  );
  // 起始值：PIE／費用／活動從涵蓋案帶入，Registration 答 Yes 做 screening 的
  // TNE／TNA 也帶（都可改）；legacy import 只問 PIE 與費用，只帶這兩組。
  useQcPrefill({
    engagements: selected,
    qcType: form.qcType,
    answers: form.answers,
    setAnswers: (updater) => setForm((prev) => ({ ...prev, answers: updater(prev.answers) })),
    legacy: !!form.legacyImport,
  });

  // 空白的必填格（含問卷逐題）：按 Create 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => qcRecordFormGaps(form, { allowMissingSignedForm: missingSignedFormAllowed }), [form, missingSignedFormAllowed]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveQcRecord(form);
      navigate(saved && saved.record ? `/qm/qc-records/${saved.record.id}` : '/qm/qc-records');
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/qm/qc-records"
      backLabel="QC6 / QC7 Records"
      title="New QC6 / QC7 Record"
      subtitle="Record a QC6 / QC7 form completed outside the system."
      error={error || validation.error || optionsError}
      onSubmit={submit}
      submitLabel="Create Record"
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
      <FormSection
        step={1}
        title="Select the Engagement(s)"
        hint={<>Engagements still without a QC6 / QC7 form, grouped by client group and financial year. <InfoTip text="One record may cover several companies of the same client group whose FYE falls in the same year — the year-end dates may differ. Engagements already covered by a QC request in flight are not listed — approve that request instead." /></>}
      >
        <QcCoverageSection
          engagements={engagements}
          selectedIds={form.engagementIds}
          onChange={(ids) => setField('engagementIds', ids)}
        />
      </FormSection>

      <FormSection
        step={2}
        title="QC Type"
        hint="QC6 = new client acceptance, QC7 = existing client continuance. It stamps the covered engagement(s) too."
      >
        <QcTypeSection
          value={form.qcType}
          onChange={(v) => setForm((prev) => ({ ...prev, qcType: v, answers: {} }))}
        />
        <QcApprovalDateSection value={form.approvedOn} onChange={(v) => setField('approvedOn', v)} />
      </FormSection>

      {/* 同 workflow QC New：選了類型才出現問卷。 */}
      {form.qcType && (
      <FormSection
        step={3}
        title="QC6 / QC7 Form"
        hint={legacyEnabled ? 'Legacy import is on: a signed form completed outside the system can be uploaded instead of filling in the whole form.' : undefined}
      >
        <QcAnswersSection
          qcType={form.qcType}
          answers={form.answers}
          onChange={(v) => setField('answers', v)}
          engagements={selected}
          legacyEnabled={legacyEnabled === true}
          legacyImport={form.legacyImport}
          onLegacyImportChange={(v) => setForm((prev) => ({ ...prev, legacyImport: v, answers: {}, signedForm: [] }))}
          signedForm={form.signedForm}
          onSignedForm={(v) => setField('signedForm', v)}
          allowMissingSignedForm={missingSignedFormAllowed}
        />
      </FormSection>
      )}

      <FormSection step={4} title="Attachments & Remarks">
        <QcAttachmentsSection
          files={form.files}
          onFiles={(v) => setField('files', v)}
          remarks={form.remarks}
          onRemarks={(v) => setField('remarks', v)}
        />
      </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default QMQcRecordNew;
